import { modalController } from '@ionic/vue';
import StripeEmbeddedCheckoutModal from '@/components/stripe/StripeEmbeddedCheckoutModal.vue';
import { cancelHighlightCheckout, pollHighlightCheckout } from '@/api/partner';
import store from '@/store';
import {
  clearStripeEmbeddedCheckoutContext,
  stashStripeEmbeddedCheckoutContext,
  STRIPE_POLL_KIND,
} from '@/utils/stripeEmbeddedCheckout';
import {
  buildPartnerStatusEventDetail,
  notifyPartnerStatusChanged,
  showPartnerCheckoutModal,
} from '@/utils/partnerMembership';

export const FULLSCREEN_MODAL_CLASS = 'fullscreen-modal';

/**
 * Drop abandoned checkout state server-side (e.g. pending highlight after cancel).
 * Polls first so a late-paid session is not force-cancelled while Stripe redirects.
 */
export const cleanupPartnerEmbeddedCheckoutCancel = async (pollKind, sessionId) => {
  if (pollKind === STRIPE_POLL_KIND.PARTNER_HIGHLIGHT) {
    if (sessionId) {
      try {
        const pollResult = await pollHighlightCheckout(sessionId);
        if (pollResult?.ready && pollResult?.purchase) {
          await showPartnerCheckoutModal('highlight_success');
          notifyPartnerStatusChanged(buildPartnerStatusEventDetail({
            purchase: pollResult.purchase,
          }));
          return { completed: true };
        }
      } catch (err) {
        console.warn('Partner highlight checkout poll before cancel failed', err);
      }
    }

    try {
      await cancelHighlightCheckout(sessionId || undefined);
    } catch (err) {
      console.warn('Partner highlight checkout cancel cleanup failed', err);
    }
  }

  await store.dispatch('partner/refreshStatus');
  notifyPartnerStatusChanged({ status: store.getters['partner/getStatus'] });
  return { completed: false };
};

/**
 * Open Stripe Embedded Checkout in a fullscreen modal (same UX in PWA, Safari, desktop).
 */
export const openStripeEmbeddedCheckout = async ({
  clientSecret,
  sessionId,
  pollKind = STRIPE_POLL_KIND.PARTNER_GENERIC,
  returnRoute = { name: 'navigation.cockpit' },
  invoiceId = '',
} = {}) => {
  if (!clientSecret) {
    return { error: 'missing_client_secret' };
  }

  stashStripeEmbeddedCheckoutContext({
    pollKind,
    returnRoute,
    sessionId,
    invoiceId,
  });

  store.commit('ui/incrementOverlay');

  const modal = await modalController.create({
    component: StripeEmbeddedCheckoutModal,
    componentProps: { clientSecret },
    cssClass: FULLSCREEN_MODAL_CLASS,
    backdropDismiss: false,
  });

  const dismissPromise = modal.onDidDismiss();
  await modal.present();
  const result = await dismissPromise;
  store.commit('ui/decrementOverlay');
  return result;
};

/**
 * Prefer embedded checkout; fall back to hosted redirect for older API responses.
 */
export const startPartnerStripeCheckout = async (checkoutResult, options = {}) => {
  if (checkoutResult?.clientSecret) {
    const result = await openStripeEmbeddedCheckout({
      clientSecret: checkoutResult.clientSecret,
      sessionId: checkoutResult.sessionId,
      pollKind: options.pollKind,
      returnRoute: options.returnRoute,
      invoiceId: options.invoiceId,
    });

    if (result?.role === 'cancel') {
      const cleanup = await cleanupPartnerEmbeddedCheckoutCancel(
        options.pollKind,
        checkoutResult.sessionId,
      );
      clearStripeEmbeddedCheckoutContext();
      if (cleanup?.completed) {
        return { role: 'complete', data: cleanup };
      }
    }

    return result;
  }

  if (checkoutResult?.url) {
    const { openPartnerStripeCheckout, markPartnerStripeCheckoutStarted, stashPartnerCheckoutReturn } = await import('@/utils/partnerCheckoutReturn');
    stashPartnerCheckoutReturn(options.returnRoute || { name: 'navigation.cockpit' });
    markPartnerStripeCheckoutStarted(options.pollKind);
    openPartnerStripeCheckout(checkoutResult.url);
    return { role: 'redirect' };
  }

  return checkoutResult;
};
